import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PokerCard } from "@/features/poker/components/PokerCard";
import {
  Trophy,
  Coins,
  Flame,
  Target,
  RotateCcw,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import type { AnswerResponse, Difficulty, PreflopAction } from "./types";

interface RoundReview {
  hand: { rank: string; suit: { name: string; symbol: string; color: string } }[];
  handNotation: string;
  position: string;
  yourAction: PreflopAction;
  correctAction: PreflopAction;
  correct: boolean;
  explanationEn: string;
  explanationEs: string;
}

interface Props {
  score: number;
  correctCount: number;
  totalRounds: number;
  bestStreak: number;
  chipsEarned: number;
  difficulty: Difficulty;
  rounds: RoundReview[];
  language: string;
  onPlayAgain: () => void;
}

export const SessionResults = ({
  score,
  correctCount,
  totalRounds,
  bestStreak,
  chipsEarned,
  difficulty,
  rounds,
  language,
  onPlayAgain,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showReview, setShowReview] = useState(false);
  const actionLabel = (a: PreflopAction) =>
    a === "fold" ? t('poker_fold') : a === "call" ? t('poker_call') : t('poker_raise');
  const percentage = Math.round((correctCount / totalRounds) * 100);

  const grade =
    percentage >= 90
      ? { label: t('preflop_result_excellent'), color: "text-green-400" }
      : percentage >= 70
        ? { label: t('preflop_result_good'), color: "text-secondary" }
        : percentage >= 50
          ? { label: t('preflop_result_practice'), color: "text-orange-400" }
          : { label: t('preflop_result_needs_work'), color: "text-red-400" };

  const wrongRounds = rounds.filter((r) => !r.correct);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="text-center pt-6 space-y-2">
          <Trophy className="h-12 w-12 text-secondary mx-auto" />
          <h1 className={`text-3xl font-bold ${grade.color}`}>{grade.label}</h1>
          <p className="text-muted-foreground">{percentage}{t('preflop_result_percent_correct')}</p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 text-center">
            <Target className="h-5 w-5 text-primary mx-auto mb-1" />
            <p className="text-2xl font-bold">
              {correctCount}/{totalRounds}
            </p>
            <p className="text-xs text-muted-foreground">{t('preflop_result_correct')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Coins className="h-5 w-5 text-secondary mx-auto mb-1" />
            <p className="text-2xl font-bold text-secondary">+{chipsEarned}</p>
            <p className="text-xs text-muted-foreground">{t('preflop_result_chips')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Flame className="h-5 w-5 text-orange-400 mx-auto mb-1" />
            <p className="text-2xl font-bold">{bestStreak}</p>
            <p className="text-xs text-muted-foreground">{t('preflop_result_streak')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Trophy className="h-5 w-5 text-primary mx-auto mb-1" />
            <p className="text-2xl font-bold">{score}</p>
            <p className="text-xs text-muted-foreground">{t('preflop_result_score')}</p>
          </Card>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="lg"
            className="py-5"
            onClick={() => navigate("/minigames")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('preflop_result_back')}
          </Button>
          <Button
            size="lg"
            className="py-5 bg-secondary text-secondary-foreground hover:bg-secondary/90"
            onClick={onPlayAgain}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('preflop_result_play_again')}
          </Button>
        </div>

        {/* Review mistakes */}
        {wrongRounds.length > 0 && (
          <div>
            <Button
              variant="ghost"
              className="w-full justify-between"
              onClick={() => setShowReview(!showReview)}
            >
              <span>{t('preflop_result_review', { count: wrongRounds.length })}</span>
              {showReview ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            {showReview && (
              <div className="space-y-3 mt-2">
                {rounds.map((r, i) => (
                  <Card
                    key={i}
                    className={`p-4 ${r.correct ? "opacity-60" : "border-red-500/30"}`}
                  >
                    <div className="flex items-center gap-3">
                      {r.correct ? (
                        <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 shrink-0" />
                      )}

                      <div className="flex gap-1.5">
                        {r.hand.map((card, j) => (
                          <PokerCard key={j} card={card} compact />
                        ))}
                      </div>

                      <Badge variant="outline" className="text-xs shrink-0">
                        {r.position}
                      </Badge>

                      <div className="flex-1 text-right text-sm">
                        {!r.correct && (
                          <div>
                            <span className="text-red-400 line-through">
                              {actionLabel(r.yourAction)}
                            </span>
                            <span className="text-green-400 ml-2">
                              {actionLabel(r.correctAction)}
                            </span>
                          </div>
                        )}
                        {r.correct && (
                          <span className="text-green-400">
                            {actionLabel(r.correctAction)}
                          </span>
                        )}
                      </div>
                    </div>

                    {!r.correct && (
                      <p className="text-xs text-muted-foreground mt-2 pl-8">
                        {language === "es" ? r.explanationEs : r.explanationEn}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
